<template>
  <div class="image-settings-container">
    <div class="form-section p-4">
      <h4 class="mb-3 letter-2px">{{ $t("imageSettings") }}</h4>
      <!-- 欲生成景點選擇 -->
      <label class="form-label" for="gen-landmarks">{{ $t("genLandmarks") }}</label>
      <div class="select-like" ref="landmarkWrap">
        <button
          id="gen-landmarks"
          type="button"
          class="form-select text-start d-flex align-items-center justify-content-between"
          @click.stop="toggleLandmarkDropdown"
        >
          <span class="text-truncate">{{ selectedLandmarkLabel }}</span>
        </button>

        <ul v-show="isLandmarkOpen" class="select-menu shadow-sm" @click.stop>
          <li class="select-item" @click="pickLandmark('noGenLandmarks')">
            {{ $t("noGenLandmarks") }}
          </li>

          <li
            v-for="(_, i18nKey) in landmarkOptions"
            :key="i18nKey"
            class="select-item"
            @click="pickLandmark(i18nKey)"
          >
            {{ $t(`${i18nKey}`) }}
          </li>
        </ul>
      </div>
    </div>

    <div class="collapsible-section">
      <div class="section-header" @click="toggleSection('basicParameter')">
        <i :class="['bi', showSections.basicParameter ? 'bi-chevron-up' : 'bi-chevron-down']"></i>
        <label for="basicParameter" class="section-title">{{ $t("basicParameter") }}</label>
      </div>
      <div v-show="showSections.basicParameter" class="section-content">
        <!-- 只有在選擇地標時才顯示年代和風格選項 -->
        <div v-if="building !== 'noGenLandmarks'" class="row">
          <div>
            <label for="style" class="form-label mb-1 mt-3">{{ $t("style") }}</label>
            <select id="style" v-model="style" class="form-select">
              <option value="" disabled selected hidden>{{ $t("pleaseSelect") }}</option>
              <option value="normal">{{ $t("normal") }}</option>
              <option value="black_and_white">{{ $t("blackAndWhite") }}</option>
              <option value="old_style">{{ $t("oldStyle") }}</option>
            </select>
          </div>
        </div>

        <div class="section-title mb-1 mt-3">
          <i
            ref="generationPreferenceTooltip"
            class="info-icon bi bi-info-circle me-2"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            :data-bs-original-title="$t('generationPreferenceInfo')"
          />
          <span>{{ $t("generationPreference") }}</span>
        </div>

        <div class="btn-group w-100 generation-preference-group">
          <input
            id="prefHistorical"
            v-model="generationPreference"
            type="radio"
            class="btn-check"
            name="generationPreference"
            value="historical"
            autocomplete="off"
          >
          <label class="btn btn-outline-secondary" for="prefHistorical">
            {{ $t("historicalPreferred") }}
          </label>

          <input
            id="prefNatural"
            v-model="generationPreference"
            type="radio"
            class="btn-check"
            name="generationPreference"
            value="natural"
            autocomplete="off"
          >
          <label class="btn btn-outline-secondary" for="prefNatural">
            {{ $t("naturalBlend") }}
          </label>

          <input
            id="prefPrompt"
            v-model="generationPreference"
            type="radio"
            class="btn-check"
            name="generationPreference"
            value="prompt"
            autocomplete="off"
          >
          <label class="btn btn-outline-secondary" for="prefPrompt">
            {{ $t("promptPriority") }}
          </label>
        </div>

        <div class="section-title mb-1 mt-3">
          <i 
            ref="iconTooltip"
            class="info-icon bi bi-info-circle me-2"
            data-bs-toggle="tooltip"
            data-bs-placement="top"
            :data-bs-original-title="$t('seedInfo')"
          />
          <label for="seed">{{ $t("seed") }}</label>
        </div>
        
        <div class="seed-group">
          <input
            id="seed"
            v-model="seed"
            type="number"
            class="form-control seed-group-input"
            :placeholder="$t('enterSeedValue')"
          >
          <!-- 骰子按鈕 -->
          <button class="btn seed-group-button" :aria-label="$t('getRandomSeed')" @click="generateRandomSeed">
            <i
              ref="diceTooltip"
              class="bi bi-dice-3-fill"
              data-bs-toggle="tooltip"
              data-bs-placement="top"
              :data-bs-original-title="$t('getRandomSeed')"
            />
          </button>
        </div>

        <div class="row">
          <div class="col-6">
            <label for="width" class="form-label mb-1 mt-3">{{ $t("imageWidth") }}</label>
            <select id="width" v-model.number="width" class="form-select">
              <option value="" disabled selected hidden>{{ $t("pleaseSelect") }}</option>
              <option value="512">512</option>
              <option value="768">768</option>
              <option value="1024">1024</option>
            </select>
          </div>
          <div class="col-6">
            <label for="height" class="form-label mb-1 mt-3">{{ $t("imageHeight") }}</label>
            <select id="height" v-model.number="height" class="form-select">
              <option value="" disabled selected hidden>{{ $t("pleaseSelect") }}</option>
              <option value="512">512</option>
              <option value="768">768</option>
              <option value="1024">1024</option>
            </select>
          </div>
        </div>

        <label for="lora" class="form-label mb-1 mt-3">{{ $t("LoRAModel") }}</label>
        <select id="lora" v-model="loraModel" class="form-select" @change="loraModelChange">
          <option value="" disabled selected hidden>{{ $t("pleaseSelect") }}</option>
          <option value="None">{{ $t("none") }}</option>
          <!-- tainan_colonial_v1.0_20250918.safetensors: 1305_台南老照片_純台南_gemma3:27b_17verPure-rank64-ep13-r4-11740.safetensors -->
          <!-- tainan_colonial_v1.1_20250925.safetensors: 1305_台南老照片_純台南_gemma3:27b_17ver_PlusJsonInfo-12914 -->
          <!-- tainan_colonial_v1.2_20260120.safetensors: 5499_聚珍台灣_純台南_gemma3:27b_17V+Json-rk64-ep3-r1-2696 -->
          <!-- tainan_colonial_v1.25_20260123.safetensors: 5499_聚珍台灣前三批_純台南_gemma3:27b_gemini_poi-rank32-ep5-r4-4125 -->
          <!-- tainan_colonial_v1.3_20260128.safetensors: 9843_基金會+聚珍台灣前三批_gemma3:27b_gemoni_poi-rank32-ep4-r1-4824 -->
          <!-- tainan_colonial_v1.4_20260209.safetensors: 9988_基金會+聚珍台灣前三批+運河主題_gemma3:27b_gemini_poi-rank32-ep4-r1-2447 -->
          <!-- tainan_colonial_v1.5_20260316.safetensors: 11985_基金會+聚珍台灣前四批+運河主題_gemma3:27b_gemini_poi-rank64-ep4-r1-5934 -->
          <!-- tainan_colonial_v1.6_20260420.safetensors: 13988_基金會+聚珍台灣前五批+運河主題_gemma3:27b_gemini_poi-rank64-ep4-r1-3462 -->
          <!-- tainan_colonial_v1.7_20260821.safetensors: 14000_基金會+聚珍台灣前五批+運河主題+民眾徵案1-12張_gemma3:27b_gemini_poi-rank64-ep4-r1-3465 -->
          <!-- tainan_colonial_v1.8_20261006.safetensors: 14010_基金會+聚珍台灣前五批+運河主題++民眾徵案1+5共22張_gemma3:27b_gemini_poi-rank64-ep4-r1-6936  -->
          <!-- <option value="tainan_colonial_v1.1_20250925.safetensors">{{ $t("tainanStyleOfTapanColonial") }} v1.1</option>
          <option value="tainan_colonial_v1.3_20260128.safetensors">{{ $t("tainanStyleOfTapanColonial") }} v1.3</option>
          <option value="tainan_colonial_v1.4_20260209.safetensors">{{ $t("tainanStyleOfTapanColonial") }} v1.4</option>
          <option value="tainan_colonial_v1.5_20260316.safetensors">{{ $t("tainanStyleOfTapanColonial") }} v1.5</option> -->
          <option value="tainan_colonial_v1.6_20260420.safetensors">{{ $t("tainanStyleOfTapanColonial") }} v1.6</option>
          <option value="tainan_colonial_v1.7_20260821.safetensors">{{ $t("tainanStyleOfTapanColonial") }} v1.7</option>
          <option value="tainan_colonial_v1.8_20261006.safetensors">{{ $t("tainanStyleOfTapanColonial") }} v1.8</option>
        </select>
      </div>
    </div>

    <div class="collapsible-section">
      <div class="section-header" @click="toggleSection('advanceParameter')">
        <i :class="['bi', showSections.advanceParameter ? 'bi-chevron-up' : 'bi-chevron-down']"></i>
        <label for="advanceParameter" class="section-title">{{ $t("advanceParameter") }}</label>
      </div>
      <div v-show="showSections.advanceParameter" class="section-content">
        <label for="loraWeight" class="form-label mb-1 mt-3">{{ $t("loraWeight") }}: {{ loraWeight }}</label>
        <input
          id="loraWeight"
          v-model.number="loraWeight"
          type="range"
          class="form-range dynamic-progress-slider"
          min="0"
          max="1"
          step="0.1"
        >
        <label for="steps" class="form-label mb-1 mt-3">{{ $t("steps") }}: {{ steps }}</label>
        <input
          id="steps"
          v-model.number="steps"
          type="range"
          class="form-range dynamic-progress-slider"
          min="20"
          max="50"
          step="1"
        >
        <label for="cfg" class="form-label mb-1 mt-3">{{ $t("cfg") }}: {{ cfg.toFixed(1) }}</label>
        <input
          id="cfg"
          v-model.number="cfg"
          type="range"
          class="form-range dynamic-progress-slider"
          min="1"
          max="20"
          step="0.1"
        >
        <label for="neg-prompt-text" class="form-label mb-1 mt-3">{{ $t("negPrompt") }}</label>
        <textarea
          id="neg-prompt-text"
          v-model="negativePrompt"
          class="form-control flex-grow-1"
          rows="3"
        />
      </div>
    </div>
    
    <div class="form-section p-4">
      <label for="prompt-text" class="form-label">{{ $t("prompt") }}</label>
      <textarea
        id="prompt-text"
        v-model="positivePrompt"
        class="form-control flex-grow-1"
        rows="3"
        @input="updatePrompt"
      />
      <div class="d-flex justify-content-center">
        <button class="btn block-btn block-btn-hover mt-3" :disabled="isLoading" @click="generateImage">
          <span v-if="isLoading"> {{ $t("generating") }}...</span>
          <span v-else>{{ $t("generateImage") }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api/axios';
import Tooltip from 'bootstrap/js/dist/tooltip';
import config from '@/config.json'
import { currentUser } from '@/auth'

export default {
  emits: ["update-image","update-current-prompt", "update-loading"], // 聲明 update 事件

  data() {
    return {
      building: "noGenLandmarks", //chihkan-tower, hayashi-department, tainan-train-station
      eras: "1910-1930", // 1910-1930, 1910before, 1930-1950 預留欄位
      style: "normal", // normal, black_and_white, old_style
      height: 1024, // 預設圖片高度
      width: 1024,  // 預設圖片寬度
      positivePrompt: "A cat flying on the sky, wearing a hat. Animation style.",
      negativePrompt: "",
      loraModel: "tainan_colonial_v1.8_20261006.safetensors", // 預設 LORA 模型
      loraWeight: 0.6,
      seed: "492147161085354",
      steps: 28,
      cfg: 7.0,
      samplerName: "euler",
      generationPreference: "historical", // denoise等級: historical,natural,prompt
      isLoading: false, // 控制加載狀態
      isQueueLoading: false, // 新按鈕的 loading 狀態
      tooltips: [],
      // 控制各個區段的展開/收合狀態
      showSections: {
        basicParameter: true,  // 預設打開
        advanceParameter: false
      },
      landmarkOptions: {
        "hayashi":"hayashi-department" , // 林百貨
        "tnnStation":"tainan-train-station",  // 台南火車站
        "tnnStationWood":"tainan-train-station",  // 台南火車站(第一代木造)
        "chihkan":"chihkan-tower", // 赤崁樓
        "greatSouthGate": "great-south-gate", // 大南門
        "tangTeChang": "Tang-TeChang", // 湯德章公園
        "tainanPGB":"Tainan-Prefectural-Government-building", // 臺南州廳(台灣文學館)
        "grandMatsuTemple":"grand-mazu-temple", // 大天后宮
        "wuFamilyGarden":"wu-family-garden", // 吳園
        "tnnDistrictCourt":"judicial-museum", // 台南地方法院(司法博物館)
        "tnnPublicHall":"public-hall", // 台南公會堂
        "tnnConfuciusTemple":"confucius-temple", // 台南孔廟
        "tnnArtMuseum":"art-museum", // 台南美術館
        "formerTnnMS":"meteorological-station", // 原台南測候所
        "anpingOldFort":"anping-old-fort", // 安平古堡
        "tnnWestMarket":"west-market", // 台南西市場
        "TainanWudeHall":"wude-hall", // 台南武德殿
        "eternalgoldenfort":"eternal-golden-fort", // 億載金城
        "tcMerchantHouse":"tait-co-merchant-house", // 德記洋行
      },
      isLandmarkOpen: false,
    };
  },
  watch: {
    "$i18n.locale": function () { // 監聽語言變更
      this.refreshTooltips(); // 重新載入 Tooltip
    },
  },

  async mounted() {
    this.tooltips = []; // 確保 tooltips 為陣列
    this.initTooltips(); // 初次初始化 Tooltip
    // 點外面就關掉 dropdown
    document.addEventListener("click", this.handleOutsideClick);
  },

  beforeUnmount() {
    document.removeEventListener("click", this.handleOutsideClick);
    this.destroyTooltips();
  },

  methods: {
    // 根據模型不同,使用不同預設參數
    loraModelChange(){
      if (this.loraModel == 'tw_colonial_v1.0_20241231.safetensors'){
        this.steps = 40;
        this.cfg = 3.5;
        this.negativePrompt = '';
      } else if (this.loraModel != 'None' ){
        this.steps = 28;
        this.cfg = 4.5;
        this.negativePrompt = 'modern clothes, modern cars, traffic lights, smartphones, skyscrapers, LED signs, plastic chairs, contemporary typography, neon glow, saturated neon, selfie, blurry, extra fingers, watermark, signature, lowres, artifacts';
      }
    },
    buildingChange(){
      if (this.building == "noGenLandmarks"){
        this.cfg = 4;  // img2img parameter
        this.style = 'normal';
      } else {
        this.cfg = 7;  // img2img parameter
      }
      // 改造前的台南火車站
      if (this.building == 'tnnStationWood'){
        this.eras = "1910before"
      } else {
        this.eras = "1910-1930"
      }
    },
    // 新增：切換區段展開/收合狀態
    toggleSection(sectionName) {
      this.showSections[sectionName] = !this.showSections[sectionName];
    },

    async generateImage() { // 請求生圖並等圖生成完畢(後端直接回傳生完的圖片)
      this.isLoading = true;
      this.$emit("update-loading", this.isLoading); //通知父元件（App.vue）更新 isLoading 狀態

      try {
        // var request_url = "/stable_diffusion/api/prompt_wait/"
        let request_url = "/stable_diffusion/api/prompt_text2img"
        let landmarks = null;

        if (this.building !== "noGenLandmarks"){
          landmarks = this.landmarkOptions[this.building]
          request_url = "/stable_diffusion/api/prompt_img2img"
        }

        const response = await api.post(
          `${config.apiBaseUrl}${request_url}`, {
            lora_model: this.loraModel,
            lora_weight: this.loraWeight,
            eras: this.eras,
            landmarks: landmarks,
            style: this.style,
            seed: this.seed,
            width: this.width,
            height: this.height,
            steps: this.steps,
            cfg: this.cfg,
            sampler_name: this.samplerName,
            generationPreference: this.generationPreference,
            positive_prompt: this.positivePrompt,
            negative_prompt: this.negativePrompt,
        },
          {
            responseType: "blob", // 設置回傳格式為二進制 Blob
          }
        );

        const blob = new Blob([response.data], { type: "image/png" });
        const imageUrl = URL.createObjectURL(blob);

        // 傳遞圖片 URL 給父組件
        this.$emit("update-image", imageUrl);

      } catch (error) {
        console.error("Failed to fetch image:", error);
      } finally {
        this.isLoading = false;
        this.$emit("update-loading", this.isLoading); //通知父元件（App.vue）更新 isLoading 狀態
      }
    },

    updatePrompt() { // 更新父元件的prompt以傳遞到ChatLog.vue中
      this.$emit("update-current-prompt", this.positivePrompt); // 通知父元件更新 prompt
    },
    generateRandomSeed() { // 隨機生成seed值      
      const array = new Uint32Array(1);
      window.crypto.getRandomValues(array);
      this.seed = array[0].toString();
    },
    initTooltips() {
      this.destroyTooltips(); // 確保不會有舊的 Tooltip

      this.$nextTick(() => {
        this.tooltips = []; // 確保 `this.tooltips` 被初始化
        const tooltipElements = [this.$refs.iconTooltip, this.$refs.diceTooltip, this.$refs.generationPreferenceTooltip];

        tooltipElements.forEach((element) => {
          if (element) {
            const tooltipInstance = new Tooltip(element, {
              trigger: "hover focus",
              title: element.getAttribute("data-bs-original-title"), // 確保讀取 Vue 動態內容
            });
            this.tooltips.push(tooltipInstance);
          }
        });
      });
    },

    destroyTooltips() {
      if (Array.isArray(this.tooltips)) {
        this.tooltips.forEach((tooltip) => tooltip.dispose()); // 安全銷毀舊 Tooltip
        this.tooltips = [];
      }
    },

    refreshTooltips() {
      this.initTooltips(); // 重新載入 Tooltip
    },

    toggleLandmarkDropdown() {
      this.isLandmarkOpen = !this.isLandmarkOpen;
    },

    pickLandmark(val) {
      // val 可能是 "" / "noGenLandmarks" / i18nKey
      this.building = val;
      this.isLandmarkOpen = false;

      this.buildingChange();
    },

    handleOutsideClick(e) {
      // 只要點擊不在容器內，就關閉
      const wrap = this.$refs.landmarkWrap;
      if (!wrap) return;

      // $refs 在 Vue 可能是 DOM 或 Component instance
      const el = wrap.$el ? wrap.$el : wrap;
      if (el && !el.contains(e.target)) {
        this.isLandmarkOpen = false;
      }
    },
  },
  computed: {
    selectedLandmarkLabel() {
      if (this.building === "noGenLandmarks") return this.$t("noGenLandmarks");
      return this.$t(`${this.building}`);
    },
    currentUser() {
      return currentUser.value
    },
  },
};
</script>

<style scoped>
/* 主容器樣式 */
.image-settings-container {
  width: 100%;
}

/* Info icon 樣式 */
.info-icon {
  color: #6c757d;
  font-size: 0.9rem;
  cursor: pointer;
  color: #6c757d;
}
/* Scrollable dropdown (select-lookalike) */
.select-like {
  position: relative;
}

/* 顯示選中值的那顆「假 select」(button) */
.select-like > button.form-select {
  /* 跟 global .form-select 一致 */
  font-size: 0.9rem !important;
  line-height: 1.5 !important;
  font-weight: 400 !important;
}

/* 保險：避免內部 span 被其他全域樣式放大 */
.select-like > button.form-select span {
  font-size: inherit !important;
  line-height: inherit !important;
}

/* 展開後的選單 */
.select-menu {
  position: absolute;
  z-index: 1050;
  left: 0;
  right: 0;
  margin-top: 0.25rem;
  padding: 0.25rem 0;
  list-style: none;

  background: #fff;
  border: 1px solid #ddd;
  border-radius: 10px;

  /* 約 8 筆 + scrollbar */
  max-height: calc(8 * 2.25rem);
  overflow-y: auto;

  /* 清單文字大小跟 select 同步 */
  font-size: 0.9rem;
  line-height: 1.5;
}

/* 選單項目 */
.select-item {
  padding: 0.45rem 0.75rem;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  font-size: inherit;
  line-height: inherit;
}

.select-item:hover {
  background: rgba(33, 33, 33, 0.06);
}

.generation-preference-group .btn {
  border-color: #c9ced6;
  border-width: 1px;
  white-space: nowrap;
  font-size: 0.95rem;
  color: #6c757d;
}

.generation-preference-group .btn-outline-secondary {
  background-color: #fff;
}

.generation-preference-group .btn-check:checked + .btn {
  background-color: #7b848d;
  border-color: #7b848d;
  color: #fff;
}
</style>